
import { useStockItems } from "@/hooks/useStockItems";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, TrendingUp, TrendingDown, AlertCircle, DollarSign, BarChart3 } from "lucide-react";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { StatsCard } from "@/components/ui/StatsCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { useRevenueData } from "@/hooks/useRevenueData";

export const StockAnalysisTab = () => {
  const { data: stockItems, isLoading: stockLoading, error: stockError } = useStockItems();
  const { data: currency } = useOwnerCurrency();
  const { currentMonthData } = useRevenueData();
  const [searchQuery, setSearchQuery] = useState("");
  const [soldItems, setSoldItems] = useState<any[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(true);
  const [salesError, setSalesError] = useState<Error | null>(null);
  const [activeTab, setActiveTab] = useState<string>("potential");

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} ${currency || 'FCFA'}`;
  };
  
  useEffect(() => {
    const fetchSoldItems = async () => {
      try {
        setIsLoadingSales(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");
        
        const now = new Date();
        const firstDayOfMonth = startOfMonth(now);
        const lastDayOfMonth = endOfMonth(now);
        
        // 1. Récupérer les transactions de type 'sale' pour le mois en cours qui ont été validées
        const { data: transactions, error } = await supabase
          .from("poultry_transactions")
          .select(`
            id,
            stock_item_id,
            quantity,
            unit_price,
            total_amount,
            transaction_date,
            is_deducted_from_revenue,
            stock_items (*)
          `)
          .eq('created_by', user.id)
          .eq('transaction_type', 'sale')
          .eq('is_deducted_from_revenue', true) // On ne prend que les transactions validées
          .gte('created_at', firstDayOfMonth.toISOString())
          .lte('created_at', lastDayOfMonth.toISOString());
          
        if (error) throw error;
        
        // 2. Récupérer les différences de comptage validées comme revenus
        const { data: discrepancies, error: discrepancyError } = await supabase
          .from("counting_discrepancies")
          .select(`
            id,
            counting_date,
            payment_method,
            expected_amount,
            counted_amount,
            status
          `)
          .eq('created_by', user.id)
          .eq('status', 'validated_as_revenue')
          .gte('counting_date', firstDayOfMonth.toISOString().split('T')[0])
          .lte('counting_date', lastDayOfMonth.toISOString().split('T')[0]);
          
        if (discrepancyError) throw discrepancyError;
        
        // Calculer l'ajustement total dû aux différences de comptage
        let countingAdjustment = 0;
        if (discrepancies && discrepancies.length > 0) {
          countingAdjustment = discrepancies.reduce((sum, disc) => {
            // Si le montant compté est différent du montant attendu, prendre en compte cette différence
            return sum + (disc.counted_amount - disc.expected_amount);
          }, 0);
        }
        
        // Regrouper les transactions par produit pour calculer les totaux
        const groupedItems = transactions.reduce((acc: any, transaction) => {
          const stockItemId = transaction.stock_item_id;
          
          if (!acc[stockItemId]) {
            acc[stockItemId] = {
              stockItem: transaction.stock_items,
              totalQuantity: 0,
              totalRevenue: 0,
              transactions: []
            };
          }
          
          acc[stockItemId].totalQuantity += transaction.quantity;
          acc[stockItemId].totalRevenue += transaction.total_amount;
          acc[stockItemId].transactions.push(transaction);
          
          return acc;
        }, {});
        
        // Convertir en tableau pour le composant
        const items = Object.values(groupedItems);
        
        // Ajouter une entrée spéciale pour les ajustements de comptage
        if (countingAdjustment !== 0) {
          items.push({
            stockItem: {
              data: { name: "Ajustements de comptage", unit: "" },
              purchase_price: 0
            },
            totalQuantity: 1,
            totalRevenue: countingAdjustment,
            isAdjustment: true,
            transactions: []
          });
        }
        
        setSoldItems(items);
      } catch (error) {
        console.error("Erreur lors de la récupération des ventes:", error);
        setSalesError(error as Error);
      } finally {
        setIsLoadingSales(false);
      }
    };
    
    fetchSoldItems();
  }, []);

  if (stockLoading || isLoadingSales) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Chargement des données...</span>
      </div>
    );
  }

  if (stockError || salesError) {
    return (
      <div className="flex justify-center items-center h-64 text-red-500">
        <AlertCircle className="h-8 w-8 mr-2" />
        Une erreur est survenue lors du chargement des données.
      </div>
    );
  }

  const filteredItems = stockItems?.filter(item => {
    if (!item || !item.data) return false;
    // Utilisation de l'opérateur de chaînage optionnel et de l'opérateur nullish coalescing
    const itemName = item.data?.name || item.data?.Race || "";
    return itemName.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  // Calculer les statistiques globales pour les produits en stock (potentiel)
  const potentialStats = filteredItems.reduce(
    (acc, item) => {
      // Utilisation de l'opérateur de chaînage optionnel pour éviter les erreurs
      const price = Number(item.data?.price) || 0;
      const purchasePrice = Number(item.purchase_price) || 0;
      const quantity = item.quantity || 0;
      
      const totalPurchaseValue = purchasePrice * quantity;
      const totalSaleValue = price * quantity;
      const totalProfit = (price - purchasePrice) * quantity;
      
      // Margin percentage
      let marginPercent = 0;
      if (price > 0 && purchasePrice >= 0) {
        marginPercent = ((price - purchasePrice) / price) * 100;
      }
      
      // Counter for negative margin products
      const isNegativeMargin = marginPercent < 0;
      
      return {
        totalPurchaseValue: acc.totalPurchaseValue + totalPurchaseValue,
        totalSaleValue: acc.totalSaleValue + totalSaleValue,
        totalProfit: acc.totalProfit + totalProfit,
        negativeMarginCount: acc.negativeMarginCount + (isNegativeMargin ? 1 : 0),
        totalMargin: acc.totalMargin + marginPercent,
        itemCount: acc.itemCount + 1
      };
    },
    { 
      totalPurchaseValue: 0, 
      totalSaleValue: 0, 
      totalProfit: 0, 
      negativeMarginCount: 0,
      totalMargin: 0,
      itemCount: 0
    }
  );
  
  // Calculer les statistiques globales pour les produits vendus (réalisé)
  const realizedStats = soldItems.reduce(
    (acc, item: any) => {
      const stockItem = item.stockItem;
      const purchasePrice = Number(stockItem?.purchase_price) || 0;
      const totalRevenue = item.totalRevenue || 0;
      const totalQuantity = item.totalQuantity || 0;
      
      // Si c'est un ajustement de comptage, pas de coût d'achat
      const totalCost = item.isAdjustment ? 0 : purchasePrice * totalQuantity;
      
      // Profit réalisé
      const profit = totalRevenue - totalCost;
      
      // Margin percentage sur les ventes réalisées
      let marginPercent = 0;
      if (totalRevenue > 0 && !item.isAdjustment) {
        marginPercent = ((totalRevenue - totalCost) / totalRevenue) * 100;
      }
      
      // Ne pas compter les ajustements dans le calcul de la marge moyenne
      const shouldCountInAverage = !item.isAdjustment;
      
      return {
        totalCost: acc.totalCost + totalCost,
        totalRevenue: acc.totalRevenue + totalRevenue,
        totalProfit: acc.totalProfit + profit,
        totalQuantity: item.isAdjustment ? acc.totalQuantity : acc.totalQuantity + totalQuantity,
        totalMargin: shouldCountInAverage ? acc.totalMargin + marginPercent : acc.totalMargin,
        itemCount: shouldCountInAverage ? acc.itemCount + 1 : acc.itemCount
      };
    },
    { 
      totalCost: 0, 
      totalRevenue: 0, 
      totalProfit: 0, 
      totalQuantity: 0,
      totalMargin: 0,
      itemCount: 0
    }
  );
  
  const averagePotentialMargin = potentialStats.itemCount > 0 
    ? potentialStats.totalMargin / potentialStats.itemCount 
    : 0;
    
  const averageRealizedMargin = realizedStats.itemCount > 0 
    ? realizedStats.totalMargin / realizedStats.itemCount 
    : 0;

  // Colonnes pour le tableau des produits en stock (potentiel)
  const potentialColumns = [
    {
      id: "product",
      label: "Produit",
      cell: (row: any) => (
        <div className="font-medium">{row.data?.name || row.data?.Race || "Produit sans nom"}</div>
      ),
    },
    {
      id: "quantity",
      label: "Quantité",
      cell: (row: any) => (
        <div className="text-center">
          {row.quantity || 0} {row.data?.unit || "pièce(s)"}
        </div>
      ),
    },
    {
      id: "purchase_price",
      label: "Prix d'achat",
      cell: (row: any) => (
        <div className="text-right font-medium">
          {formatCurrency(Number(row.purchase_price) || 0)}
        </div>
      ),
    },
    {
      id: "sale_price",
      label: "Prix de vente",
      cell: (row: any) => (
        <div className="text-right font-medium">
          {formatCurrency(Number(row.data?.price) || 0)}
        </div>
      ),
    },
    {
      id: "margin",
      label: "Marge",
      cell: (row: any) => {
        const purchasePrice = Number(row.purchase_price) || 0;
        const salePrice = Number(row.data?.price) || 0;
        
        if (salePrice === 0) return <div className="text-right">N/A</div>;
        
        const marginPercent = ((salePrice - purchasePrice) / salePrice) * 100;
        const marginValue = salePrice - purchasePrice;
        
        const textColorClass = 
          marginPercent < 0 ? "text-red-500" :
          marginPercent < 15 ? "text-orange-500" :
          "text-green-600";
        
        return (
          <div className={`text-right ${textColorClass} font-medium`}>
            {marginPercent.toFixed(1)}% ({formatCurrency(marginValue)})
          </div>
        );
      },
    },
    {
      id: "total_purchase_value",
      label: "Valeur d'achat totale",
      cell: (row: any) => {
        const purchasePrice = Number(row.purchase_price) || 0;
        const quantity = row.quantity || 0;
        const totalValue = purchasePrice * quantity;
        
        return (
          <div className="text-right font-medium">
            {formatCurrency(totalValue)}
          </div>
        );
      },
    },
    {
      id: "total_sale_value",
      label: "Valeur de vente totale",
      cell: (row: any) => {
        const salePrice = Number(row.data?.price) || 0;
        const quantity = row.quantity || 0;
        const totalValue = salePrice * quantity;
        
        return (
          <div className="text-right font-medium">
            {formatCurrency(totalValue)}
          </div>
        );
      },
    },
    {
      id: "potential_profit",
      label: "Bénéfice potentiel",
      cell: (row: any) => {
        const purchasePrice = Number(row.purchase_price) || 0;
        const salePrice = Number(row.data?.price) || 0;
        const quantity = row.quantity || 0;
        const profit = (salePrice - purchasePrice) * quantity;
        
        const textColorClass = profit < 0 ? "text-red-500" : "text-green-600";
        
        return (
          <div className={`text-right ${textColorClass} font-bold`}>
            {formatCurrency(profit)}
          </div>
        );
      },
    },
  ];
  
  // Colonnes pour le tableau des produits vendus (réalisé)
  const realizedColumns = [
    {
      id: "product",
      label: "Produit",
      cell: (item: any) => (
        <div className="font-medium">
          {item.isAdjustment 
            ? <span className="italic">{item.stockItem.data.name}</span>
            : item.stockItem?.data?.name || item.stockItem?.data?.Race || "Produit sans nom"}
        </div>
      ),
    },
    {
      id: "quantity",
      label: "Quantité vendue",
      cell: (item: any) => (
        <div className="text-center">
          {item.isAdjustment 
            ? <span className="italic">-</span>
            : `${item.totalQuantity || 0} ${item.stockItem?.data?.unit || "pièce(s)"}`}
        </div>
      ),
    },
    {
      id: "purchase_price",
      label: "Prix d'achat unitaire",
      cell: (item: any) => (
        <div className="text-right font-medium">
          {item.isAdjustment 
            ? <span className="italic">-</span>
            : formatCurrency(Number(item.stockItem?.purchase_price) || 0)}
        </div>
      ),
    },
    {
      id: "total_cost",
      label: "Coût total d'achat",
      cell: (item: any) => {
        if (item.isAdjustment) {
          return <div className="text-right font-medium italic">-</div>;
        }
        
        const purchasePrice = Number(item.stockItem?.purchase_price) || 0;
        const totalCost = purchasePrice * item.totalQuantity;
        
        return (
          <div className="text-right font-medium">
            {formatCurrency(totalCost)}
          </div>
        );
      },
    },
    {
      id: "total_revenue",
      label: "Revenu total",
      cell: (item: any) => {
        const revenue = item.totalRevenue || 0;
        const textClass = item.isAdjustment && revenue < 0 ? "text-red-500" : 
                         item.isAdjustment ? "italic" : "";
        
        return (
          <div className={`text-right font-medium ${textClass}`}>
            {formatCurrency(revenue)}
          </div>
        );
      },
    },
    {
      id: "realized_profit",
      label: "Bénéfice réalisé",
      cell: (item: any) => {
        const purchasePrice = Number(item.stockItem?.purchase_price) || 0;
        const totalCost = item.isAdjustment ? 0 : purchasePrice * item.totalQuantity;
        const profit = item.totalRevenue - totalCost;
        
        const textColorClass = profit < 0 ? "text-red-500" : "text-green-600";
        const isAdjustmentClass = item.isAdjustment ? "italic" : "";
        
        return (
          <div className={`text-right ${textColorClass} font-bold ${isAdjustmentClass}`}>
            {formatCurrency(profit)}
          </div>
        );
      },
    },
    {
      id: "margin_percentage",
      label: "Marge réalisée",
      cell: (item: any) => {
        if (item.isAdjustment) {
          return <div className="text-right italic">-</div>;
        }
        
        const purchasePrice = Number(item.stockItem?.purchase_price) || 0;
        const totalCost = purchasePrice * item.totalQuantity;
        const totalRevenue = item.totalRevenue || 0;
        
        if (totalRevenue === 0) return <div className="text-right">N/A</div>;
        
        const marginPercent = ((totalRevenue - totalCost) / totalRevenue) * 100;
        
        const textColorClass = 
          marginPercent < 0 ? "text-red-500" :
          marginPercent < 15 ? "text-orange-500" :
          "text-green-600";
        
        return (
          <div className={`text-right ${textColorClass} font-medium`}>
            {marginPercent.toFixed(1)}%
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-bold">Analyse de rentabilité des produits</h2>
        <p className="text-muted-foreground">
          Suivez la rentabilité potentielle et réalisée de vos produits pour optimiser votre stratégie commerciale.
        </p>
        
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="bg-violet-300 hover:bg-violet-200">
            <TabsTrigger value="potential">Rentabilité potentielle</TabsTrigger>
            <TabsTrigger value="realized">Rentabilité réalisée</TabsTrigger>
          </TabsList>
          
          <TabsContent value="potential">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatsCard
                  title="Valeur d'achat totale"
                  value={formatCurrency(potentialStats.totalPurchaseValue)}
                  icon={TrendingDown}
                  description="Investissement total en stock"
                />
                <StatsCard
                  title="Valeur de vente totale"
                  value={formatCurrency(potentialStats.totalSaleValue)}
                  icon={TrendingUp}
                  description="Revenus potentiels du stock"
                />
                <StatsCard
                  title="Bénéfice potentiel"
                  value={formatCurrency(potentialStats.totalProfit)}
                  icon={TrendingUp}
                  description="Profits potentiels après vente"
                />
                <StatsCard
                  title="Marge moyenne"
                  value={`${averagePotentialMargin.toFixed(2)}%`}
                  icon={AlertCircle}
                  description={`${potentialStats.negativeMarginCount} produits à marge négative`}
                />
              </div>
              
              <div className="mb-4">
                <Input
                  placeholder="Rechercher un produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-sm"
                />
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Détail de la rentabilité potentielle par produit</CardTitle>
                </CardHeader>
                <CardContent>
                  <DataTable
                    columns={potentialColumns}
                    data={filteredItems}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="realized">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatsCard
                  title="Produits vendus"
                  value={`${realizedStats.totalQuantity}`}
                  icon={BarChart3}
                  description="Quantité totale vendue ce mois"
                />
                <StatsCard
                  title="Coût total d'achat"
                  value={formatCurrency(realizedStats.totalCost)}
                  icon={TrendingDown}
                  description="Coût des produits vendus"
                />
                <StatsCard
                  title="Revenu total"
                  value={formatCurrency(realizedStats.totalRevenue)}
                  icon={DollarSign}
                  description="Chiffre d'affaires réalisé"
                />
                <StatsCard
                  title="Bénéfice réalisé"
                  value={formatCurrency(realizedStats.totalProfit)}
                  icon={TrendingUp}
                  description={`Marge moyenne: ${averageRealizedMargin.toFixed(2)}%`}
                />
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Détail de la rentabilité réalisée ce mois</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 text-sm text-muted-foreground">
                    <p>
                      Cette analyse inclut uniquement les transactions validées comme chiffre d'affaires et prend en compte les ajustements effectués lors du comptage.
                    </p>
                  </div>
                  {soldItems.length > 0 ? (
                    <DataTable
                      columns={realizedColumns}
                      data={soldItems}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucune vente validée ce mois-ci.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
